import React, { createContext, useContext } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import type { DraggableAttributes } from '@dnd-kit/core'

// ─── Drag Handle Context ──────────────────────────────────────────────────────

interface DragHandleContextValue {
  listeners: SyntheticListenerMap | undefined
  attributes: DraggableAttributes
}

const DragHandleContext = createContext<DragHandleContextValue>({
  listeners: undefined,
  attributes: {} as DraggableAttributes,
})

/**
 * Consume drag-handle listeners/attributes inside a widget.
 * Spread them on the element that should act as the drag handle.
 */
export const useDragHandle = () => useContext(DragHandleContext)

// ─── SortableCard ─────────────────────────────────────────────────────────────

export interface SortableCardProps {
  id: string
  children: React.ReactNode
  className?: string
}

/**
 * Wrap any dashboard card with this component to make it sortable within its
 * section.  The card's existing `<CardShifting />` drag icon is wired up via
 * `useDragHandle()` – this component merely provides the context + outer div.
 *
 * Must be rendered inside a `<DndContext>` + `<SortableContext>`.
 */
export const SortableCard = ({
  id,
  children,
  className = '',
}: SortableCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: 'relative',
  }

  return (
    <DragHandleContext.Provider value={{ listeners, attributes }}>
      <div ref={setNodeRef} style={style} className={className}>
        {children}
      </div>
    </DragHandleContext.Provider>
  )
}
